import React, { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ADMISSION_METHOD } from 'shared/constants';
import { useClaimFeatureAccess } from '../../hooks/useClaimFeatureAccess';

const FALLBACK_PATH = '/insurance-portal';
// Stable id so a rapid re-mount / StrictMode double-invoke shows a single toast.
const ACCESS_DENIED_TOAST_ID = 'claim-feature-access-denied';

// Resolve whether the route's payment method is enabled for the company.
// Unknown method (shared create routes) falls back to "any feature active" so a
// legitimate user is never hard-blocked when the method cannot be determined.
function isMethodAllowed(method, access) {
  if (method === ADMISSION_METHOD.CASHLESS) return access.isCashlessActive;
  if (method === ADMISSION_METHOD.REIMBURSEMENT) {
    return access.isReimbursementActive;
  }
  return access.isAnyClaimFeatureActive;
}

/**
 * Route guard that gates Create/Detail claim routes by the company's feature
 * access (SA-1514). When `method` is omitted, the payment method is derived from
 * the `?payment=` query param or the `paymentMethod` set by the claim-type modal.
 */
const ClaimFeatureGuard = ({ method, children }) => {
  const access = useClaimFeatureAccess();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const resolvedMethod =
    method ??
    searchParams.get('payment') ??
    localStorage.getItem('paymentMethod') ??
    null;

  const isAllowed = isMethodAllowed(resolvedMethod, access);

  // Notify the user why they were bounced before redirecting to the list.
  useEffect(() => {
    if (!isAllowed) {
      toast.error(t('error_claim_feature_access_denied'), {
        toastId: ACCESS_DENIED_TOAST_ID,
      });
    }
  }, [isAllowed, t]);

  if (!isAllowed) {
    return <Navigate to={FALLBACK_PATH} replace />;
  }

  return children;
};

export default ClaimFeatureGuard;
